import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { cn } from "../../../utils/cn";

export interface TabItem {
  id: string;
  label: React.ReactNode;
  content: React.ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  defaultActiveId?: string;
}

export function Tabs({ tabs, defaultActiveId }: TabsProps) {
  const tabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);
  const [active, setActive] = useState(defaultActiveId ?? tabIds[0]);
  const current = useMemo(() => tabs.find((t) => t.id === active), [tabs, active]);
  const [fade, setFade] = useState(0);
  // Sync when defaultActiveId or available tabs change
  useEffect(() => {
    setActive((prev) => {
      if (defaultActiveId !== undefined && tabIds.includes(defaultActiveId)) {
        return defaultActiveId;
      }
      if (prev !== undefined && tabIds.includes(prev)) {
        return prev;
      }
      return tabIds[0];
    });
  }, [defaultActiveId, tabIds]);

  // Fade-in animation on tab change
  useEffect(() => {
    setFade(0);
    const t = setTimeout(() => setFade(1), 0);
    return () => clearTimeout(t);
  }, [active]);
  const focusTab = useCallback((id: string | undefined) => {
    if (!id || typeof document === "undefined") return;
    const el = document.getElementById(`tab-${id}`);
    if (el instanceof HTMLButtonElement) {
      el.focus();
    }
  }, []);
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!tabIds.length) return;
    const activeIndex = active === undefined ? -1 : tabIds.indexOf(active);
    const currentIndex = activeIndex === -1 ? 0 : activeIndex;
    if (e.key === "ArrowRight") {
      const nextId = tabIds[(currentIndex + 1) % tabIds.length];
      setActive(nextId);
      focusTab(nextId);
      e.preventDefault();
    } else if (e.key === "ArrowLeft") {
      const prevId = tabIds[(currentIndex - 1 + tabIds.length) % tabIds.length];
      setActive(prevId);
      focusTab(prevId);
      e.preventDefault();
    } else if (e.key === "Home") {
      const firstId = tabIds[0];
      setActive(firstId);
      focusTab(firstId);
      e.preventDefault();
    } else if (e.key === "End") {
      const lastId = tabIds[tabIds.length - 1];
      setActive(lastId);
      focusTab(lastId);
      e.preventDefault();
    }
  };
  return (
    <div>
      <div role="tablist" className="flex gap-2 border-b border-muted" onKeyDown={onKeyDown}>
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            id={`tab-${t.id}`}
            aria-controls={`panel-${t.id}`}
            tabIndex={active === t.id ? 0 : -1}
            aria-selected={active === t.id}
            onClick={() => setActive(t.id)}
            className={cn(
              "rounded-t-sm px-3 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              active === t.id
                ? "border-b-2 border-accent text-accent"
                : "text-muted hover:text-accent",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {current && (
        <div
          role="tabpanel"
          id={`panel-${current.id}`}
          aria-labelledby={`tab-${current.id}`}
          className="p-4 transition-opacity duration-200 motion-reduce:transition-none"
          style={{ opacity: fade }}
        >
          {current.content}
        </div>
      )}
    </div>
  );
}

export default Tabs;
